import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, Camera, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function TestAttempt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState("instructions"); // instructions | test | result
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [violations, setViolations] = useState(0);
  const [result, setResult] = useState(null);
  const startRef = useRef(0);

  useEffect(() => { api.get(`/tests/${id}/attempt`).then(({ data }) => { setTest(data.test); setQuestions(data.questions); }); }, [id]);

  const submit = useCallback(async (auto = false) => {
    const time_taken = Math.round((Date.now() - startRef.current) / 1000);
    const { data } = await api.post(`/tests/${id}/submit`, { answers, time_taken, violations });
    setResult(data);
    setStage("result");
    if (auto) toast.error("Test auto-submitted due to violations");
  }, [answers, id, violations, test]);

  useEffect(() => {
    if (stage !== "test") return;
    const t = setInterval(() => setTimeLeft((s) => { if (s <= 1) { clearInterval(t); submit(true); return 0; } return s - 1; }), 1000);
    return () => clearInterval(t);
  }, [stage, submit]);

  // proctoring: tab switch detection
  useEffect(() => {
    if (stage !== "test" || !test?.proctoring?.tab_switch) return;
    const handler = () => {
      if (document.hidden) {
        setViolations((v) => {
          const nv = v + 1;
          const max = test.proctoring?.max_violations || 3;
          toast.warning(`Warning ${nv}/${max}: Do not switch tabs!`);
          if (nv >= max) submit(true);
          return nv;
        });
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [stage, test, submit]);

  const start = () => { startRef.current = Date.now(); setTimeLeft((test.duration || 30) * 60); setStage("test"); };
  const setAns = (qid, val, multi) => setAnswers((a) => {
    if (multi) { const cur = a[qid] || []; return { ...a, [qid]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] }; }
    return { ...a, [qid]: [val] };
  });

  if (!test) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 border-4 border-amber border-t-transparent rounded-full animate-spin" /></div>;

  if (stage === "instructions")
    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="test-instructions">
        <div className="bg-white border border-border rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-navy">{test.title}</h1>
          <div className="grid grid-cols-3 gap-4 mt-6 text-center">
            <div className="bg-muted rounded-lg p-4"><div className="font-heading text-2xl font-black text-navy">{test.duration}</div><div className="text-xs text-muted-foreground">Minutes</div></div>
            <div className="bg-muted rounded-lg p-4"><div className="font-heading text-2xl font-black text-navy">{questions.length}</div><div className="text-xs text-muted-foreground">Questions</div></div>
            <div className="bg-muted rounded-lg p-4"><div className="font-heading text-2xl font-black text-navy">{test.total_marks}</div><div className="text-xs text-muted-foreground">Marks</div></div>
          </div>
          <div className="mt-6 text-sm text-navy/80"><h3 className="font-semibold mb-2">Instructions</h3><p>{test.instructions}</p></div>
          {(test.proctoring?.camera || test.proctoring?.tab_switch) && (
            <div className="mt-4 bg-crimson/5 border border-crimson/20 rounded-lg p-4 text-sm">
              <p className="font-semibold text-crimson flex items-center gap-2"><Camera size={16} /> Proctoring Enabled</p>
              <ul className="mt-2 space-y-1 text-navy/70 text-xs">
                {test.proctoring?.camera && <li>• Camera monitoring is required</li>}
                {test.proctoring?.fullscreen && <li>• Full-screen mode recommended</li>}
                {test.proctoring?.tab_switch && <li>• Tab switching is monitored (max {test.proctoring?.max_violations || 3} violations)</li>}
              </ul>
            </div>
          )}
          <Button className="w-full mt-6 viriks-amber text-white font-semibold" onClick={start} data-testid="start-test-btn">Start Test</Button>
        </div>
      </div>
    );

  if (stage === "result")
    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="test-result">
        <div className="bg-white border border-border rounded-2xl p-8 text-center">
          {result.passed ? <CheckCircle2 size={56} className="text-emerald-500 mx-auto" /> : <XCircle size={56} className="text-crimson mx-auto" />}
          <h1 className="font-heading text-2xl font-bold text-navy mt-3">{result.status === "pending_review" ? "Submitted for Review" : result.passed ? "Congratulations!" : "Keep Practicing"}</h1>
          <div className="font-heading text-5xl font-black text-navy mt-4">{result.percentage}%</div>
          <p className="text-muted-foreground">Score: {result.score} / {result.total}</p>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-emerald-50 rounded-lg p-3"><div className="font-bold text-emerald-600 text-xl">{result.correct}</div><div className="text-xs">Correct</div></div>
            <div className="bg-red-50 rounded-lg p-3"><div className="font-bold text-crimson text-xl">{result.wrong}</div><div className="text-xs">Wrong</div></div>
            <div className="bg-gray-50 rounded-lg p-3"><div className="font-bold text-gray-500 text-xl">{result.unattempted}</div><div className="text-xs">Skipped</div></div>
          </div>
          <div className="flex gap-3 justify-center mt-8">
            <Button className="viriks-navy text-white" onClick={() => navigate("/learner/results")}>View All Results</Button>
            <Button variant="outline" onClick={() => navigate("/tests")}>More Tests</Button>
          </div>
        </div>
      </div>
    );

  const q = questions[idx];
  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");
  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="test-taking">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium">Question {idx + 1} of {questions.length}</span>
        <span className={`flex items-center gap-2 font-mono font-bold ${timeLeft < 60 ? "text-crimson" : "text-navy"}`}><Clock size={16} /> {mm}:{ss}</span>
      </div>
      <Progress value={((idx + 1) / questions.length) * 100} className="mb-6" />
      {violations > 0 && <div className="mb-4 flex items-center gap-2 text-crimson text-sm"><AlertTriangle size={15} /> Violations: {violations}</div>}
      <div className="bg-white border border-border rounded-xl p-6">
        <h2 className="font-semibold text-navy text-lg">{q.question}</h2>
        <div className="mt-5 space-y-2">
          {q.options.map((opt) => {
            const multi = q.type === "multiple";
            const selected = (answers[q.id] || []).includes(opt);
            return (
              <button key={opt} onClick={() => setAns(q.id, opt, multi)} data-testid={`option-${opt}`}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${selected ? "border-amber bg-amber/10" : "border-border hover:border-navy/30"}`}>
                <span className={`inline-block h-4 w-4 mr-3 border-2 ${multi ? "rounded" : "rounded-full"} ${selected ? "bg-amber border-amber" : "border-gray-300"}`} />
                {opt}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between mt-6">
        <Button variant="outline" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}>Previous</Button>
        {idx < questions.length - 1 ? (
          <Button className="viriks-navy text-white" onClick={() => setIdx((i) => i + 1)} data-testid="next-question">Next</Button>
        ) : (
          <Button className="viriks-amber text-white font-semibold" onClick={() => submit(false)} data-testid="submit-test">Submit Test</Button>
        )}
      </div>
    </div>
  );
}
